/* TanStack Query hooks, one per operation. Keys live in `queryKeys`; every mutation that
   answers with the enquiry writes it into the cache (so `useNextStep().goNext(enquiry)` can
   resolve without a round trip) and invalidates the views derived from it. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { DocumentInput, UploadOptions } from './client';
import { useMutation } from './guardedMutation';
import type {
  BookingReferenceRequest,
  ChooseLegRequest,
  ContactRequest,
  CreateEnquiryRequest,
  DataDisputeRequest,
  EligibilityBasisRequest,
  Document,
  Enquiry,
  EventsRequest,
  ExtraRequest,
  HandoffRequest,
  ItemDecisionRequest,
  ItemKey,
  LegalAnswerRequest,
  NextStepQuery,
  PartLaterRequest,
  PassengerInput,
  PassengerPart,
  PassengersRequest,
  ResultResponseRequest,
  SelfReportRequest,
  SendLinkRequest,
  SignatureRequest,
  TripInput,
  Visit,
  WhatsappRequest,
} from './contract';
import { useApi } from './context';
import type { ScanInput } from './client';
import { rememberEnquiry } from '../funnel/session';
import { signerTimeZone } from '../format';

const norm = (q: string) => q.trim().toLowerCase();

/** Query keys in one place so invalidation stays consistent. Everything about an enquiry starts with `['enquiry', id]`. */
export const queryKeys = {
  airports: (q: string) => ['airports', norm(q)] as const,
  airlines: (q: string) => ['airlines', norm(q)] as const,
  enquiry: (id: string) => ['enquiry', id] as const,
  nextStep: (id: string, query: NextStepQuery = {}) => ['enquiry', id, 'next-step', query] as const,
  resume: (id: string) => ['enquiry', id, 'resume'] as const,
  claim: (id: string, visit: Visit) => ['enquiry', id, 'claim', visit] as const,
  legFlights: (id: string, leg: number, date?: string) =>
    ['enquiry', id, 'legs', leg, 'flights', date ?? null] as const,
  flightLookup: (id: string, leg: number, ident: string, date?: string) =>
    ['enquiry', id, 'legs', leg, 'lookup', ident, date ?? null] as const,
  assignment: (id: string, passenger?: number) => ['enquiry', id, 'assignment', passenger ?? null] as const,
  document: (id: string, documentId: string) => ['enquiry', id, 'documents', documentId] as const,
  part: (id: string, n: number) => ['enquiry', id, 'part', n] as const,
  partAssignment: (id: string, n: number) => ['enquiry', id, 'part', n, 'assignment'] as const,
  partDocument: (id: string, n: number, documentId: string) =>
    ['enquiry', id, 'part', n, 'documents', documentId] as const,
};

/** Views derived from the enquiry: everything under `['enquiry', id]` except flight searches and documents. */
function isView(key: QueryKey, id: string, except?: QueryKey) {
  if (key[0] !== 'enquiry' || key[1] !== id) return false;
  if (key[2] === 'legs' || key[2] === 'documents' || (key[2] === 'part' && key[4] === 'documents'))
    return false;
  return !except || JSON.stringify(key) !== JSON.stringify(except);
}

/** Refetch what depends on the enquiry (after a change the server made, e.g. a document check). */
export function invalidateEnquiry(qc: QueryClient, id: string, except?: QueryKey) {
  return qc.invalidateQueries({ predicate: (q) => isView(q.queryKey, id, except) });
}

/** Put a fresh enquiry in the cache and refresh the views derived from it. */
export function storeEnquiry(qc: QueryClient, e: Enquiry) {
  qc.setQueryData(queryKeys.enquiry(e.id), e);
  void invalidateEnquiry(qc, e.id, queryKeys.enquiry(e.id));
}

const HOUR = 60 * 60 * 1000;
/** How often an upload's check is polled while `processing`. */
export const DOCUMENT_POLL_MS = 1000;

/* ── Queries ─────────────────────────────────────────────── */

export function useAirportSearch(q: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.airports(q),
    queryFn: ({ signal }) => api.searchAirports({ q, signal }),
    enabled: q.trim().length > 0,
    staleTime: HOUR,
  });
}

export function useAirlineSearch(q: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.airlines(q),
    queryFn: ({ signal }) => api.searchAirlines({ q, signal }),
    enabled: q.trim().length > 0,
    staleTime: HOUR,
  });
}

/** The enquiry (the resolver's input). Disabled while `id` is null (before S3 creates it). */
export function useEnquiry(id: string | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.enquiry(id ?? ''),
    queryFn: ({ signal }) => api.getEnquiry(id!, { signal }),
    enabled: !!id,
  });
}

/** The server's resolver answer (`GET …/next-step`). Screens usually use `useNextStep()` instead. */
export function useNextStepQuery(id: string | null | undefined, query: NextStepQuery = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nextStep(id ?? '', query),
    queryFn: ({ signal }) => api.getNextStep(id!, query, { signal }),
    enabled: !!id,
  });
}

export function useResume(id: string | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.resume(id ?? ''),
    queryFn: ({ signal }) => api.getResume(id!, { signal }),
    enabled: !!id,
  });
}

/** S3: flights on a leg (`date` only for an overnight connection's next day). Cached: each search can cost a provider call. */
export function useLegFlights(id: string | null | undefined, leg: number, date?: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.legFlights(id ?? '', leg, date),
    queryFn: ({ signal }) => api.searchLegFlights(id!, leg, date ? { date } : {}, { signal }),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * S3-manual: one flight number. Pass null until both carrier and number are filled in.
 * `date` as for `useLegFlights` (an overnight connection's next day, legs after the first).
 */
export function useFlightLookup(
  id: string | null | undefined,
  leg: number,
  flight: { carrier: string; number: string } | null,
  date?: string,
) {
  const api = useApi();
  const ident = flight ? `${flight.carrier}${flight.number}` : '';
  return useQuery({
    queryKey: queryKeys.flightLookup(id ?? '', leg, ident, date),
    queryFn: ({ signal }) =>
      api.lookupLegFlight(id!, leg, { ...flight!, ...(date ? { date } : {}) }, { signal }),
    enabled: !!id && !!flight,
    staleTime: 10 * 60 * 1000,
  });
}

/** S11 (and S11-sign-for with `passenger`): PDF link and the per-market wording. */
function zone(): { timeZone?: string } {
  const timeZone = signerTimeZone();
  return timeZone ? { timeZone } : {};
}

export function useAssignmentPreview(id: string | null | undefined, passenger?: number) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.assignment(id ?? '', passenger),
    /* Q47: the viewer's zone, so the unsigned document is dated today where they are. */
    queryFn: ({ signal }) =>
      api.getAssignmentPreview(id!, { ...(passenger ? { passenger } : {}), ...zone() }, { signal }),
    enabled: !!id,
  });
}

/** S12. `visit` defaults to `returning` like the API. */
export function useClaim(id: string | null | undefined, visit: Visit = 'returning') {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.claim(id ?? '', visit),
    queryFn: ({ signal }) => api.getClaim(id!, { visit }, { signal }),
    enabled: !!id,
  });
}

export function usePassengerPart(id: string | null | undefined, n: number | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.part(id ?? '', n ?? 0),
    queryFn: ({ signal }) => api.getPassengerPart(id!, n!, { signal }),
    enabled: !!id && !!n,
  });
}

export function usePartAssignmentPreview(id: string | null | undefined, n: number | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.partAssignment(id ?? '', n ?? 0),
    queryFn: ({ signal }) => api.getPartAssignmentPreview(id!, n!, { signal }, zone()),
    enabled: !!id && !!n,
  });
}

/**
 * An upload's check, polled every second while `processing`. When it settles, the enquiry
 * (items) and its views are refetched. `passenger` for a fellow passenger's own upload (S15).
 */
export function useDocument(
  id: string | null | undefined,
  documentId: string | null | undefined,
  passenger?: number,
) {
  const api = useApi();
  const qc = useQueryClient();
  const key = passenger
    ? queryKeys.partDocument(id ?? '', passenger, documentId ?? '')
    : queryKeys.document(id ?? '', documentId ?? '');
  return useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => {
      const before = qc.getQueryData<Document>(key);
      const doc = passenger
        ? await api.getPartDocument(id!, passenger, documentId!, { signal })
        : await api.getDocument(id!, documentId!, { signal });
      if (before?.status === 'processing' && doc.status !== 'processing') void invalidateEnquiry(qc, id!);
      return doc;
    },
    enabled: !!id && !!documentId,
    refetchInterval: (q) => (q.state.data?.status === 'processing' ? DOCUMENT_POLL_MS : false),
  });
}

/* ── Mutations ───────────────────────────────────────────── */

/** A mutation answering with the enquiry: cache it, refresh its views. */
function useEnquiryMutation<V>(id: string | null | undefined, fn: (id: string, vars: V) => Promise<Enquiry>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: V) => {
      if (!id) throw new Error('No enquiry id');
      return fn(id, vars);
    },
    onSuccess: (e) => storeEnquiry(qc, e),
  });
}

/** S3, first search: creates the enquiry (this browser gets the claimant grant) and remembers its id. */
export function useCreateEnquiry() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEnquiryRequest) => api.createEnquiry(body),
    onSuccess: (e) => {
      rememberEnquiry(e.id);
      qc.setQueryData(queryKeys.enquiry(e.id), e);
    },
  });
}

/** S0b: creates the enquiry from a boarding pass; poll `useDocument(enquiry.id, document.id)` next. */
export function useCreateEnquiryFromScan() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ onProgress, signal, ...input }: ScanInput & UploadOptions) =>
      api.createEnquiryFromScan(input, { onProgress, signal }),
    onSuccess: ({ enquiry, document }) => {
      rememberEnquiry(enquiry.id);
      qc.setQueryData(queryKeys.enquiry(enquiry.id), enquiry);
      qc.setQueryData(queryKeys.document(enquiry.id, document.id), document);
    },
  });
}

export function useUpdateTrip(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: TripInput) => api.updateTrip(i, body));
}

export function useChooseLeg(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, { leg, ...body }: ChooseLegRequest & { leg: number }) =>
    api.chooseLeg(i, leg, body as ChooseLegRequest),
  );
}

export function useSetResultResponse(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: ResultResponseRequest) => api.setResultResponse(i, body));
}

export function useSetSelfReport(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: SelfReportRequest) => api.setSelfReport(i, body));
}

export function useSetLegalAnswer(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: LegalAnswerRequest) => api.setLegalAnswer(i, body));
}

/** S7. Answers `{ enquiry, existingClaim }`; the enquiry now carries the verdict. */
export function useSetContact(id: string | null | undefined) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContactRequest) => api.setContact(id!, body),
    onSuccess: (r) => storeEnquiry(qc, r.enquiry),
  });
}

export function useSetPassengers(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: PassengersRequest) => api.setPassengers(i, body));
}

export function useAddPassenger(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: PassengerInput) => api.addPassenger(i, body));
}

export function useSetBookingReference(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: BookingReferenceRequest) => api.setBookingReference(i, body));
}

export function useSignClaim(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: SignatureRequest) => api.signClaim(i, body));
}

export function useSignForMinor(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, { n, ...body }: SignatureRequest & { n: number }) =>
    api.signForMinor(i, n, body),
  );
}

export function useSetHandoff(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, { n, ...body }: HandoffRequest & { n: number }) =>
    api.setHandoff(i, n, body),
  );
}

/** S11-handoff / S12 "Send it again": `{ n, via: 'email', email? }` or `{ n, via: 'shared' }` → `shareUrl`. */
export function useSendPassengerLink(id: string | null | undefined) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ n, ...body }: SendLinkRequest & { n: number }) =>
      api.sendPassengerLink(id!, n, body as SendLinkRequest),
    onSuccess: (r) => storeEnquiry(qc, r.enquiry),
  });
}

export function useDecideItem(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, { itemKey, ...body }: ItemDecisionRequest & { itemKey: ItemKey }) =>
    api.decideItem(i, itemKey, body),
  );
}

export function useSetExtra(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: ExtraRequest) => api.setExtra(i, body));
}

/** S8 (w5-06, Q6): claim the disrupted leg alone (`leg`), or go back to the journey. */
export function useSetEligibilityBasis(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: EligibilityBasisRequest) => api.setEligibilityBasis(i, body));
}

/** S8 (w5-06, Q5): "Think our data is wrong? Tell us" — the note and any uploaded evidence. */
export function useSubmitDataDispute(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: DataDisputeRequest) => api.submitDataDispute(i, body));
}

export function useSetWhatsapp(id: string | null | undefined) {
  const api = useApi();
  return useEnquiryMutation(id, (i, body: WhatsappRequest) => api.setWhatsapp(i, body));
}

export function usePostEvents(id: string | null | undefined) {
  const api = useApi();
  return useMutation({ mutationFn: (body: EventsRequest) => api.postEvents(id!, body) });
}

/** A mutation answering with a fellow passenger's page: cache it, refresh the rest. */
function usePartMutation<V, R>(
  id: string | null | undefined,
  n: number | null | undefined,
  fn: (id: string, n: number, vars: V) => Promise<R>,
  partOf: (r: R) => PassengerPart,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: V) => fn(id!, n!, vars),
    onSuccess: (r) => {
      qc.setQueryData(queryKeys.part(id!, n!), partOf(r));
      void invalidateEnquiry(qc, id!, queryKeys.part(id!, n!));
    },
  });
}

export function useSignPart(id: string | null | undefined, n: number | null | undefined) {
  const api = useApi();
  return usePartMutation(
    id,
    n,
    (i, k, body: SignatureRequest) => api.signPart(i, k, body),
    (p) => p,
  );
}

export function useDeferPartId(id: string | null | undefined, n: number | null | undefined) {
  const api = useApi();
  return usePartMutation(
    id,
    n,
    (i, k, body: PartLaterRequest) => api.deferPartId(i, k, body),
    (r) => r.part,
  );
}

export function useExchangeLink() {
  const api = useApi();
  return useMutation({ mutationFn: (token: string) => api.exchangeLink(token) });
}

export function useRenewLink() {
  const api = useApi();
  return useMutation({ mutationFn: (token: string) => api.renewLink(token) });
}

/* ── Uploads: send, then poll the check ───────────────────── */

export type UploadTarget =
  | {
      enquiryId: string | null | undefined;
      purpose: DocumentInput['purpose'];
      itemKey?: DocumentInput['itemKey'];
    }
  | { enquiryId: string | null | undefined; passenger: number };

export type UploadStatus = 'idle' | 'uploading' | 'checking' | 'accepted' | 'rejected' | 'error';

/**
 * S10, S13, S15: `const up = useDocumentUpload({ enquiryId, purpose: 'id', itemKey: 'id' })`,
 * then `up.upload(file)`. `status` walks idle → uploading (with `progress` 0…1) → checking
 * (polled) → accepted | rejected (`document.rejectReason`) | error (`error`). `checkingSince`
 * lets the screen switch to "still working" after its budget (~10 s). When the check
 * settles, the enquiry and its views are refetched.
 */
export function useDocumentUpload(target: UploadTarget) {
  const api = useApi();
  const qc = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [checkingSince, setCheckingSince] = useState<number | null>(null);
  const passenger = 'passenger' in target ? target.passenger : undefined;
  const id = target.enquiryId;
  const abort = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: (file: File) => {
      abort.current?.abort();
      abort.current = new AbortController();
      const opts = { onProgress: setProgress, signal: abort.current.signal };
      if (!id) throw new Error('No enquiry id');
      return 'passenger' in target
        ? api.uploadPartDocument(id, target.passenger, { file }, opts)
        : api.uploadDocument(id, { file, purpose: target.purpose, itemKey: target.itemKey }, opts);
    },
    onSuccess: (doc) => {
      const key = passenger
        ? queryKeys.partDocument(id!, passenger, doc.id)
        : queryKeys.document(id!, doc.id);
      qc.setQueryData(key, doc);
      setDocumentId(doc.id);
      setCheckingSince(Date.now());
      if (doc.status !== 'processing') void invalidateEnquiry(qc, id!);
    },
  });
  const doc = useDocument(id, documentId, passenger);
  useEffect(() => () => abort.current?.abort(), []);

  const document = doc.data ?? null;
  const status: UploadStatus = mutation.isPending
    ? 'uploading'
    : mutation.isError || doc.isError
      ? 'error'
      : !document
        ? 'idle'
        : document.status === 'processing'
          ? 'checking'
          : document.status;

  const { reset: resetMutation, mutateAsync } = mutation;
  const reset = useCallback(() => {
    abort.current?.abort();
    resetMutation();
    setDocumentId(null);
    setProgress(0);
    setCheckingSince(null);
  }, [resetMutation]);

  return {
    upload: mutateAsync,
    status,
    progress,
    document,
    checkingSince: status === 'checking' ? checkingSince : null,
    error: mutation.error ?? doc.error ?? null,
    reset,
  };
}
